<template>
  <div>
    <!-- Page Header -->
    <div class="d-flex align-center mb-6">
      <div>
        <h1 class="text-h5 font-weight-bold">History</h1>
        <p class="text-body-2 grey--text mt-1">All label generation and print events</p>
      </div>
      <v-spacer />
      <v-menu offset-y>
        <template v-slot:activator="{ on }">
          <v-btn small outlined rounded v-on="on" class="text-none">
            <v-icon left small>mdi-export</v-icon>
            Export
          </v-btn>
        </template>
        <v-list dense>
          <v-list-item @click="exportHistory('csv')">
            <v-list-item-icon><v-icon small>mdi-file-delimited</v-icon></v-list-item-icon>
            <v-list-item-title>CSV</v-list-item-title>
          </v-list-item>
          <v-list-item @click="exportHistory('excel')">
            <v-list-item-icon><v-icon small>mdi-file-excel</v-icon></v-list-item-icon>
            <v-list-item-title>Excel</v-list-item-title>
          </v-list-item>
        </v-list>
      </v-menu>
    </div>

    <v-card class="elevation-0">
      <!-- Filters -->
      <v-card-text class="pb-0">
        <v-row>
          <v-col cols="6" md="3">
            <v-select
              v-model="filterProduct"
              :items="productOptions"
              label="Product"
              outlined
              dense
              hide-details
              rounded
            />
          </v-col>
          <v-col cols="6" md="3">
            <v-select
              v-model="filterAction"
              :items="actionOptions"
              label="Status"
              outlined
              dense
              hide-details
              rounded
            />
          </v-col>
          <v-col cols="12" md="4">
            <v-text-field
              v-model="search"
              placeholder="Search serial..."
              outlined
              dense
              hide-details
              rounded
              prepend-inner-icon="mdi-magnify"
              clearable
            />
          </v-col>
          <v-col cols="12" md="2">
            <v-btn block rounded color="primary" @click="fetchHistory" :loading="loading" class="text-none">
              Refresh
            </v-btn>
          </v-col>
        </v-row>
      </v-card-text>

      <!-- Bulk actions -->
      <v-card-text v-if="selected.length" class="pb-0 pt-2">
        <v-btn rounded color="primary" small class="text-none mr-2" @click="reprintSelected" :loading="printingBulk">
          <v-icon left small>mdi-printer</v-icon>
          Reprint selected ({{ selected.length }})
        </v-btn>
      </v-card-text>

      <v-data-table
        v-model="selected"
        :headers="headers"
        :items="filteredHistory"
        :search="search"
        :items-per-page="25"
        :loading="loading"
        show-select
        item-key="_uid"
        class="mt-2"
      >
        <template v-slot:item.serial_number="{ item }">
          <span class="monospace font-weight-medium">{{ item.serial_number }}</span>
        </template>
        <template v-slot:item.status="{ item }">
          <v-chip
            v-if="item.generated"
            color="primary"
            x-small
            dark
            class="font-weight-medium mr-1"
          >
            generated
          </v-chip>
          <v-chip
            v-if="item.printed"
            color="success"
            x-small
            dark
            class="font-weight-medium mr-1"
          >
            printed
          </v-chip>
          <v-chip
            v-if="item.imported"
            color="warning"
            x-small
            dark
            class="font-weight-medium"
          >
            imported
          </v-chip>
        </template>
        <template v-slot:item.printed_label_size="{ item }">
          <span class="text-body-2">{{ item.printed_label_size || '—' }}</span>
        </template>
        <template v-slot:item.last_activity="{ item }">
          <span class="text-body-2">{{ formatDate(item.last_activity) }}</span>
        </template>
        <template v-slot:item.actions="{ item }">
          <v-tooltip bottom>
            <template v-slot:activator="{ on }">
              <v-btn icon small v-on="on" @click="previewLabel(item)" class="mr-1">
                <v-icon>mdi-eye-outline</v-icon>
              </v-btn>
            </template>
            <span>Preview</span>
          </v-tooltip>
          <v-tooltip bottom>
            <template v-slot:activator="{ on }">
              <v-btn icon small v-on="on" :href="downloadUrl(item.serial_number)" target="_blank" class="mr-1">
                <v-icon>mdi-download-outline</v-icon>
              </v-btn>
            </template>
            <span>Download PDF</span>
          </v-tooltip>
          <v-tooltip bottom>
            <template v-slot:activator="{ on }">
              <v-btn
                icon
                small
                v-on="on"
                @click="reprintLabel(item)"
                :loading="printing[item.serial_number]"
              >
                <v-icon>mdi-printer-outline</v-icon>
              </v-btn>
            </template>
            <span>Reprint</span>
          </v-tooltip>
        </template>
        <template v-slot:no-data>
          <div class="text-center pa-8">
            <v-icon size="48" color="grey lighten-1">mdi-history</v-icon>
            <div class="text-body-2 grey--text mt-3">No events found</div>
          </div>
        </template>
      </v-data-table>
    </v-card>

    <LabelPreview v-model="previewDialog" :serial="previewSerial" />
  </div>
</template>

<script>
import api from '../api';
import LabelPreview from './LabelPreview.vue';

var API_URL = process.env.VUE_APP_API_URL || 'http://localhost:8001';

export default {
  name: 'HistoryTable',
  components: { LabelPreview: LabelPreview },
  data: function () {
    return {
      history: [],
      loading: false,
      search: '',
      filterProduct: 'All',
      filterAction: 'All',
      actionOptions: ['All', 'generated', 'printed', 'imported'],
      headers: [
        { text: 'Serial Number', value: 'serial_number' },
        { text: 'Product', value: 'product_code' },
        { text: 'Status', value: 'status', sortable: false },
        { text: 'Label Size', value: 'printed_label_size', sortable: true },
        { text: 'Last Activity', value: 'last_activity' },
        { text: '', value: 'actions', sortable: false, align: 'end', width: 150 },
      ],
      printing: {},
      selected: [],
      printingBulk: false,
      previewDialog: false,
      previewSerial: null,
    };
  },
  computed: {
    products: function () {
      return this.$store.state.products;
    },
    productOptions: function () {
      return ['All'].concat(this.products);
    },
    filteredHistory: function () {
      var fp = this.filterProduct;
      var fa = this.filterAction;
      return this.history.filter(function (h) {
        if (fp !== 'All' && h.product_code !== fp) return false;
        if (fa !== 'All' && !h[fa]) return false;
        return true;
      });
    },
  },
  mounted: function () {
    this.fetchHistory();
  },
  methods: {
    fetchHistory: function () {
      var self = this;
      self.loading = true;
      api
        .get('/history/all')
        .then(function (res) {
          self.history = res.data.history.map(function (h, i) {
            h._uid = i;
            h.last_activity = h.printed_at || h.generated_at || h.imported_at || '';
            return h;
          });
        })
        .catch(function () {})
        .finally(function () {
          self.loading = false;
        });
    },
    formatDate: function (ts) {
      if (!ts) return '';
      return new Date(ts).toLocaleString();
    },
    previewLabel: function (item) {
      this.previewSerial = item.serial_number;
      this.previewDialog = true;
    },
    downloadUrl: function (serial) {
      return API_URL + '/api/labelbase/label/download/' + serial + '?label_size=12mm&download=true';
    },
    reprintSelected: function () {
      var self = this;
      self.printingBulk = true;
      var serials = self.selected.map(function (s) { return s.serial_number; });
      // deduplicate
      var unique = serials.filter(function (v, i, a) { return a.indexOf(v) === i; });
      var chain = Promise.resolve();
      unique.forEach(function (serial) {
        chain = chain.then(function () {
          return self.reprintLabel({ serial_number: serial });
        });
      });
      chain.finally(function () {
        self.printingBulk = false;
        self.selected = [];
      });
    },
    reprintLabel: function (item) {
      var self = this;
      self.$set(self.printing, item.serial_number, true);
      var imgUrl = API_URL + '/api/labelbase/label/image/' + item.serial_number + '?label_size=12mm';
      fetch(imgUrl)
        .then(function (resp) {
          if (!resp.ok) throw new Error('Failed to fetch label image');
          return resp.blob();
        })
        .then(function (blob) {
          var form = new FormData();
          form.append('file', blob, item.serial_number + '.png');
          form.append('size', '12mm');
          return fetch('http://localhost:8081/serial/print-image', { method: 'POST', body: form });
        })
        .then(function (printResp) {
          if (!printResp.ok) throw new Error('Print failed');
          return api.post('/label/print', { serial: item.serial_number, label_size: '12mm' });
        })
        .then(function () {
          self.$set(self.printing, item.serial_number, false);
          self.fetchHistory();
        })
        .catch(function () {
          self.$set(self.printing, item.serial_number, false);
        });
    },
    exportHistory: function (format) {
      var token = this.$store.state.token;
      window.open(
        API_URL + '/api/labelbase/history/export?format=' + format + '&token=' + token,
        '_blank',
      );
    },
  },
};
</script>

<style scoped>
.monospace { font-family: 'Roboto Mono', monospace; font-size: 13px; }
</style>
